import { AuthQueryClientProvider, ProtectedRoute } from '@caterinx/api-client';
import { Toaster } from '@caterinx/ui/components/ui/toaster';
import { type QueryClientConfig } from '@tanstack/react-query';
import { Route, Routes } from 'react-router-dom';

import { AcceptInvitePage } from './pages/auth/AcceptInvitePage.js';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage.js';
import { LoginPage } from './pages/auth/LoginPage.js';
import { RegisterPage } from './pages/auth/RegisterPage.js';
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage.js';
import { CommissionsPage } from './pages/CommissionsPage.js';
import { ExternalInvitePage } from './pages/ExternalInvitePage.js';
import { PortalPage } from './pages/PortalPage.js';
import { ProfileWizardPage } from './pages/ProfileWizardPage.js';
import { RfqStatusPage } from './pages/RfqStatusPage.js';
import { SupplierRfqDetailPage } from './pages/SupplierRfqDetailPage.js';
import { SupplierRfqsPage } from './pages/SupplierRfqsPage.js';
import { TeamPage } from './pages/TeamPage.js';

/**
 * `retry: false` ist Absicht: Die Axios-Instanz erneuert das Token bei einem 401 bereits
 * selbst. Ein zusätzlicher Query-Retry darüber würde denselben Request mehrfach in
 * denselben Fehler laufen lassen — in Atleo hat genau diese Kombination aus Polling und
 * Retry das WAF-Rate-Limit gerissen und Nutzern minutenlang "Netzwerkfehler" gezeigt.
 */
const queryConfig: QueryClientConfig = {
  defaultOptions: {
    queries: { retry: false, staleTime: 30_000, refetchOnWindowFocus: false },
  },
};

export function App() {
  return (
    <AuthQueryClientProvider config={queryConfig}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        {/*
          Öffentlich, und das muss es sein: Wer eine Einladung öffnet, hat naturgemäß noch
          kein Konto. Der Pfad ist deutsch, weil er in E-Mails an Gastronomen steht.
        */}
        <Route path="/einladung" element={<AcceptInvitePage />} />
        {/*
          Ebenfalls öffentlich, aus demselben Grund eine Ebene weiter gedacht: Kunden bekommen
          im MVP überhaupt kein Konto (D6). Der Magic-Link aus der Bestätigungsmail ist ihre
          Anmeldung; das Token prüft der Handler serverseitig gegen den gespeicherten Hash.
        */}
        <Route path="/anfrage/:rfqId" element={<RfqStatusPage />} />
        {/*
          Ebenfalls öffentlich, und zwar aus einem handfesten Grund: Der Magic-Link aus der
          Einladungsmail führt direkt in die Angebotsmaske (D7). Ein Konto hat der Lieferant,
          aber in einem Sechs-Stunden-Fenster ist ein vergessenes Passwort ein verlorenes
          Angebot. Ohne Token lädt die Seite über die angemeldete Sitzung — dieselbe
          Komponente, ein Header Unterschied.
        */}
        <Route path="/ausschreibung/:rfqId" element={<SupplierRfqDetailPage />} />
        {/*
          Ebenfalls öffentlich: Die Einladung per E-Mail (D32) geht an einen Betrieb, der noch
          kein Konto hat — der Link aus der Mail muss den Auftrag zeigen, bevor jemand sich
          registriert. Der Pfad ist deutsch, weil er in Mails an Gastronomen steht. Das Token
          prüft der Handler serverseitig; angenommen wird erst nach der Anmeldung.
        */}
        <Route path="/auftrag/:rfqId" element={<ExternalInvitePage />} />

        <Route element={<ProtectedRoute loginPath="/login" />}>
          <Route path="/" element={<PortalPage />} />
          <Route path="/profil" element={<ProfileWizardPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/ausschreibungen" element={<SupplierRfqsPage />} />
          {/*
            Provisionen und Rechnungen (Phase 6). Bewusst **hinter** dem Login und ohne
            Magic-Link-Variante: Ein Link, der die gesamte Rechnungshistorie eines Betriebs
            öffnet, hätte ein ganz anderes Gewicht als einer, der zu genau einer
            Angebotsmaske führt (D7) — und er stünde in einer Mail, die weitergeleitet wird.
          */}
          <Route path="/provisionen" element={<CommissionsPage />} />
        </Route>

        <Route path="*" element={<LoginPage />} />
      </Routes>
      <Toaster />
    </AuthQueryClientProvider>
  );
}
